import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import {
  filterValidPaymentMethods,
  requiresBuyerToken,
  sortPaymentOptions,
  type PaymentOption,
} from '@src/domains/payments';
import type { Transactable } from '@src/domains/transactables';
import { loadPaymentOptions, paymentOptionSelector } from '@src/features/payments/store/payment';

import { usePaymentFingerprints } from './usePaymentFingerprints';
import { useSelectedMethodId } from './useSelectedMethodId';

export const usePaymentOptions = (transactable: Transactable) => {
  const dispatch = useAppDispatch();
  const [selectedId, setSelectedId] = useSelectedMethodId();

  const load = useAppSelector(paymentOptionSelector(transactable));
  const buyerToken = useAppSelector((state) => state.auth.buyerToken);
  const waitingForToken = requiresBuyerToken(transactable) && buyerToken == null;

  const options = useMemo(() => (load ? sortPaymentOptions(filterValidPaymentMethods(load.list)) : []), [load?.list]);

  useEffect(() => {
    if (waitingForToken || load) {
      return;
    }

    dispatch(loadPaymentOptions(transactable));
  }, [transactable, load, waitingForToken, dispatch]);

  usePaymentFingerprints({ options, ready: Boolean(load && !load.loading) });

  const selected = useMemo(
    () => options.find((option) => option.paymentMethod.id === selectedId),
    [options, selectedId],
  );

  useEffect(() => {
    if (options.length > 0 && !selected) {
      setSelectedId(options[0]!.paymentMethod.id);
    }
  }, [options, selected, setSelectedId]);

  const select = useCallback((option: PaymentOption) => setSelectedId(option.paymentMethod.id), [setSelectedId]);

  return {
    options,
    loading: waitingForToken || (load?.loading ?? true),
    selected,
    select,
  };
};
